import type { BaseConfig, Headers, UserInfo, UserPerm } from './declare'
import { makeObservable, observable, computed, runInAction, reaction, toJS } from 'mobx'
import storage from 'localforage'
import { api, $api, $consts, $cookie } from 'src/plugins'
import {
  generateLevelMenus,
  sortMenusWithField,
  generateFlatMenus,
} from 'enasops/utils/tools'
import { getHashQueryParams } from 'src/utils/tools'
import { pick } from 'lodash'

class MAINStore {
  /**
   * 将初始化的步骤移动到 constructor 中，
   * 并且请务必初始化，否则 babel 插件 plugin-transform-flow-strip-types 编译失败，
   * 相关联 issue: 
   * https://github.com/react-navigation/react-navigation/issues/5825#issuecomment-590723220
   * https://github.com/facebook/react-native/issues/20150#issuecomment-417858270
   */
  /**
   * 更新对象类型的 store 时候，
   * 除非要观察整个对象，否则禁止完全重写整个对象，
   * 因为完全重写会导致观察该对象的所有组件都重新渲染
   */
  loginModel: any
  baseConfig: BaseConfig
  userinfo: UserInfo
  userPerm: UserPerm
  headers: Headers
  menus: any[]
  activeMenu: any

  constructor () {
    this.loginModel = {}
    this.baseConfig = {}
    this.userinfo = {}
    this.userPerm = {}
    this.headers = {}
    this.menus = []
    this.activeMenu = {} // 当前激活的菜单

    makeObservable(this, {
      baseConfig: observable,
      userinfo: observable,
      userPerm: observable,
      headers: observable,
      menus: observable,
      activeMenu: observable,
      isLogin: computed,
    })
  }

  willMount () {
    const proFunc = [
      storage.getItem($consts['STORAGE/CACHE_LOGIN_MODEL']),
      storage.getItem($consts['STORAGE/CACHE_HEADERS']),
      $api['app/getConfig'](),
    ]

    const search = getHashQueryParams()
    search?.Sign && proFunc.push(
      new Promise(resolve => {
        const signData = $cookie.getItem(search.Sign)
        $cookie.removeItem(search.Sign)
        resolve(signData ? JSON.parse(signData) : null)
      })
    )

    return Promise.all(proFunc).then((datas: any[]) => {
      let [loginModel, headers, baseConfig, signData] = datas as any
      loginModel = Object.assign({}, loginModel, signData?.loginModel)
      headers = Object.assign({}, headers, signData?.headers)

      this.setLoginModel(loginModel)
      this.setHeaders(headers)
      this.setBaseConfig(baseConfig)
    })
  }

  get isLogin () {
    return !!this.headers.Authorization
  }

  setRequestParams () {
    api.setRequestParams({
      headers: Object.assign({}, this.headers, { AppCode: $consts['CONFIG/APP_CODE'] }),
      dataPayload: pick(this.userPerm, ['DataRule', 'OrgRule'])
    })
  }

  // 基础配置信息
  setBaseConfig (config: BaseConfig) {
    runInAction(() => {
      this.baseConfig = Object.assign({}, this.baseConfig, config)
    })
  }

  // 用户信息
  setUserInfo (userinfo: UserInfo) {
    runInAction(() => {
      this.userinfo = Object.assign({}, this.userinfo, userinfo)
    })
  }

  // 用户权限
  setUserPerm (userPerm: UserPerm) {
    runInAction(() => {
      this.userPerm = Object.assign({}, this.userPerm, userPerm)
    })
  }

  // 请求头
  setHeaders (headers: Headers) {
    runInAction(() => {
      this.headers = Object.assign({}, this.headers, headers)
    })
    storage.setItem($consts['STORAGE/CACHE_HEADERS'], toJS(this.headers))
  }

  // menus 拍平的一维数组
  get flatMenus () {
    const flatMenus: any[] = []
    generateFlatMenus(this.menus, flatMenus)
    return flatMenus
  }

  // 当前激活的菜单的顶级父菜单
  get activeTopMenu () {
    return this.flatMenus.find(menu => menu.ID === this.activeMenu.ID)
  }

  // 当前激活菜单
  setActiveMenu (menu: any) {
    runInAction(() => {
      this.activeMenu = menu
    })
  }

  setMenus (menus: any[]) {
    generateLevelMenus(menus)
    sortMenusWithField(menus)
    runInAction(() => {
      this.menus = menus
    })
  }

  setLoginModel (loginModel: any) {
    this.loginModel = loginModel
    storage.setItem($consts['STORAGE/CACHE_LOGIN_MODEL'], loginModel)
  }

  setDocTitle (title?: string) {
    if (title) {
      document.getElementsByTagName('title')[0].innerText = title
    }
  }

  login () {
    return $api['app/getDataByPost'](this.loginModel, {
      url: '/OP/Login/Login',
      fullData: true
    }).then((res: any) => {
      if (res && res.IsSuccessful) {
        const { BaseConfig, DataRule, OrgRule, Nav_RolePerm, Menus, User } = res.Data
        this.setDocTitle(BaseConfig?.SCREEN_TITLE)
        this.setBaseConfig(BaseConfig)
        this.setUserInfo(User)
        this.setUserPerm({ DataRule, OrgRule, Nav_RolePerm })
        this.setMenus(Menus)
        return res
      }
      return Promise.reject({ message: res.error_description })
    })
  }

  logout () {
    return storage.clear().finally(() => {
      api.unsetRequestParams('headers')
      runInAction(() => {
        this.headers = {}
      })
    })
  }
}

const mainStore = new MAINStore()

reaction(
  () => {
    const { headers, userPerm } = mainStore
    return JSON.stringify({ headers, userPerm })
  },
  () => mainStore.setRequestParams()
)

// 登录时，自动获取用户信息
reaction(
  () => mainStore.isLogin,
  isLogin => isLogin && mainStore.login()
)

export default mainStore